import { Box, FormControl, Grid, Card, CardContent, Typography, Chip, Avatar, CircularProgress } from "@mui/material";
import MaterialTable from "material-table";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import { useState, useEffect } from "react";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import CustomToggle from "../../../Base/views/CustomToggle";
import { GetTargetAchievementAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import { ConvertDateTime, getUserData } from "../../../Base/helper/baseFunctions";
import Chart from "react-apexcharts";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import VideoCameraFrontOutlinedIcon from "@mui/icons-material/VideoCameraFrontOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ─── Brand ─────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const CHART_PALETTE = ["#8B44FF", "#4E79A7", "#10B981", "#F28E2B", "#E15759", "#76B7B2"];
const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}`;
};
const fyStart = () => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-04-01`;
};
const sum = (arr, field) => arr.reduce((s, r) => s + (parseInt(r[field]) || 0), 0);
const pct = (num, den) => den > 0 ? `${Math.round((num / den) * 100)}%` : "0%";

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, sub, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 40, height: 40 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.3rem" lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                    {sub && <Typography fontSize="0.68rem" color={color} fontWeight={600}>{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const YTDClosuresNewReport = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const user = getUserData();
    const [userData, setUserData] = useState({ load: true, values: [] });
    const [filters, setFilters] = useState({
        from_date: fyStart(),
        to_date: todayStr(),
        primary_recruiter_filter: "",
    });

    const { atsTargetAchievement, isTargetRepLoading } = useSelector(state => state.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData } = useSelector(state => state.AtsReducer?.AtsReportReducer);

    // ── Load recruiter dropdown ───────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) setUserData({ load: false, values: atsRecruiterData });
    }, [atsRecruiterData]);

    // ── Fetch YTD data (FY Apr 1 → today) ────────────────────────────────────
    useEffect(() => {
        dispatch(GetTargetAchievementAction({
            from_date: filters.from_date,
            to_date: filters.to_date,
            primary_recruiter: filters.primary_recruiter_filter
                ? `{${filters.primary_recruiter_filter.ats_users_id}}`
                : null,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.from_date, filters.to_date, filters.primary_recruiter_filter]);

    const handleFilter = (event, value) => {
        if (["primary_recruiter_filter"].includes(event)) {
            setFilters(f => ({ ...f, primary_recruiter_filter: value || "" }));
        }
        if (["from_date", "to_date"].includes(event?.target?.name)) {
            setFilters(f => ({ ...f, [event.target.name]: event.target.value }));
        }
    };

    // ── Data ──────────────────────────────────────────────────────────────────
    const rows = atsTargetAchievement || [];

    // KPIs
    const totalProposed = sum(rows, "shortlisted");
    const totalRejected = sum(rows, "rejected");
    const totalInterviewed = sum(rows, "interviews_scheduled");
    const totalSelected = sum(rows, "selected");

    // ── Aggregate by recruiter for charts ─────────────────────────────────────
    const recruiterMap = {};
    rows?.forEach(r => {
        const name = r.recruiter_name || "Unknown";
        if (!recruiterMap[name]) recruiterMap[name] = { proposed: 0, rejected: 0, interviewed: 0, selected: 0 };
        recruiterMap[name].proposed   += parseInt(r.shortlisted) || 0;
        recruiterMap[name].rejected   += parseInt(r.rejected) || 0;
        recruiterMap[name].interviewed += parseInt(r.interviews_scheduled) || 0;
        recruiterMap[name].selected   += parseInt(r.selected) || 0;
    });
    const recruiterNames = Object.keys(recruiterMap).slice(0, 12);

    // Stacked bar: Proposed > Interviewed > Selected per recruiter
    const stackedBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false }, stacked: true },
            colors: [BRAND, "#4E79A7", "#10B981", "#EF4444"],
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: {
                categories: recruiterNames.map(n => n.split(" ")[0]),
                labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Proposed", data: recruiterNames.map(n => recruiterMap[n].proposed) },
            { name: "Interviewed", data: recruiterNames.map(n => recruiterMap[n].interviewed) },
            { name: "Selected", data: recruiterNames.map(n => recruiterMap[n].selected) },
            { name: "Rejected", data: recruiterNames.map(n => recruiterMap[n].rejected) },
        ],
    };

    // Funnel donut: Proposed → Interviewed → Selected
    const funnelDonut = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: ["Proposed", "Interviewed", "Selected", "Rejected"],
            colors: [BRAND, "#4E79A7", "#10B981", "#EF4444"],
            dataLabels: { formatter: (val, opts) => opts.w.globals.series[opts.seriesIndex] },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: {
                pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total Proposed", fontSize: "11px", color: "#94A3B8" } } } },
            },
        },
        series: [totalProposed, totalInterviewed, totalSelected, totalRejected],
    };

    // Monthly trend: group rows by month from modified_date
    const monthMap = {};
    rows?.forEach(r => {
        if (!r.modified_date) return;
        const d = new Date(r.modified_date);
        if (isNaN(d)) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,"0")}`;
        if (!monthMap[key]) monthMap[key] = { proposed: 0, selected: 0 };
        monthMap[key].proposed += parseInt(r.shortlisted) || 0;
        monthMap[key].selected += parseInt(r.selected) || 0;
    });
    const monthKeys = Object.keys(monthMap)?.sort();
    const areaChart = {
        options: {
            chart: { type: "area", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND, "#10B981"],
            fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 95] } },
            stroke: { curve: "smooth", width: 2.5 },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            markers: { size: 4, strokeWidth: 0 },
            xaxis: {
                categories: monthKeys.length ? monthKeys : ["No Data"],
                labels: { style: { fontSize: "10px", colors: "#94A3B8" } },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Candidates Proposed", data: monthKeys.map(k => monthMap[k].proposed) },
            { name: "Candidates Selected", data: monthKeys.map(k => monthMap[k].selected) },
        ],
    };

    // Recruiter donut by proposed count
    const top6Recruiters = Object.entries(recruiterMap)
        ?.sort(([, a], [, b]) => b.proposed - a.proposed)
        .slice(0, 6);
    const recruiterDonut = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: top6Recruiters.map(([n]) => n.split(" ")[0]),
            colors: CHART_PALETTE.slice(0, top6Recruiters.length || 1),
            dataLabels: { formatter: (val, opts) => opts.w.globals.series[opts.seriesIndex] },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: {
                pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Proposed", fontSize: "11px", color: "#94A3B8" } } } },
            },
        },
        series: top6Recruiters.map(([, v]) => v.proposed),
    };

    // Top performers by selected
    const topPerformers = Object.entries(recruiterMap)
        ?.sort(([, a], [, b]) => b.selected - a.selected)
        .slice(0, 5);

    // ── Table columns (same fields as AtsTargetAchievementReports) ────────────
    const isGroupOrg = user?.grouporg_flow === true;
    const columns = [
        {
            title: "Date", field: "modified_date",
            render: r => ConvertDateTime(r?.modified_date),
            cellStyle: { padding: "10px", whiteSpace: "nowrap" }, headerStyle: { padding: "10px" },
        },
        { title: "Recruiter", field: "recruiter_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        { title: isGroupOrg ? "Group Org" : "Client", field: "company_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        { title: "Job Title", field: "job_title", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        {
            title: "Proposed", field: "shortlisted", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={parseInt(r.shortlisted) || 0} size="small" sx={{ bgcolor: (parseInt(r.shortlisted) || 0) > 0 ? "#F3EDFD" : "#F1F5F9", color: (parseInt(r.shortlisted) || 0) > 0 ? BRAND : "#94A3B8", fontWeight: 700 }} />,
        },
        {
            title: "Interviewed", field: "interviews_scheduled", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={parseInt(r.interviews_scheduled) || 0} size="small" sx={{ bgcolor: (parseInt(r.interviews_scheduled) || 0) > 0 ? "#EFF6FF" : "#F1F5F9", color: (parseInt(r.interviews_scheduled) || 0) > 0 ? "#4E79A7" : "#94A3B8", fontWeight: 700 }} />,
        },
        {
            title: "Selected", field: "selected", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={parseInt(r.selected) || 0} size="small" sx={{ bgcolor: (parseInt(r.selected) || 0) > 0 ? "#ECFDF5" : "#F1F5F9", color: (parseInt(r.selected) || 0) > 0 ? "#059669" : "#94A3B8", fontWeight: 700 }} />,
        },
        {
            title: "Rejected", field: "rejected", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={parseInt(r.rejected) || 0} size="small" sx={{ bgcolor: (parseInt(r.rejected) || 0) > 0 ? "#FEF2F2" : "#F1F5F9", color: (parseInt(r.rejected) || 0) > 0 ? "#EF4444" : "#94A3B8", fontWeight: 700 }} />,
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters & View Toggle ─────────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Recruiter"
                            value={filters.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name"
                            required={false}
                            variant="outlined"
                            name="primary_recruiter_filter"
                            onChangeEvent={handleFilter}
                            data={userData}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.5}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="from_date" label="From Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy"
                                DefaultDTvalue={filters.from_date}
                                maxDate={new Date()}
                                handleChange={value =>
                                    handleFilter({ target: { name: "from_date", value: value ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}` : "" } })
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.5}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="to_date" label="To Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy"
                                DefaultDTvalue={filters.to_date}
                                maxDate={new Date()}
                                minDate={filters.from_date || null}
                                handleChange={value =>
                                    handleFilter({ target: { name: "to_date", value: value ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}` : "" } })
                                }
                            />
                        </FormControl>
                    </Grid>
                    {isTargetRepLoading && (
                        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={22} disableShrink/>
                        </Grid>
                    )}  
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <CustomToggle
                                isChecked={!localIsDashboardView}
                                handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                Yestext={"Table"}
                                NoText={"Dashboard"}
                                YesTooltip={"Switch to Table View"}
                                NoTooltip={"Switch to Dashboard View"}
                                style={{ height: "37px", labelPadding: "2px 8px" }}
                            />
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<PeopleOutlinedIcon sx={{ fontSize: 20 }} />} value={totalProposed} label="Candidates Proposed" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<VideoCameraFrontOutlinedIcon sx={{ fontSize: 20 }} />} value={totalInterviewed} label="Interviews Scheduled" sub={pct(totalInterviewed, totalProposed) + " of proposed"} color={BRAND} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<CheckCircleOutlinedIcon sx={{ fontSize: 20 }} />} value={totalSelected} label="Candidates Selected" sub={pct(totalSelected, totalProposed) + " selection rate"} color="#10B981" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<CancelOutlinedIcon sx={{ fontSize: 20 }} />} value={totalRejected} label="Candidates Rejected" sub={pct(totalRejected, totalProposed) + " rejection rate"} color="#EF4444" />
                            </Grid>
                        </Grid>

                        {/* ── Charts Row 1 ──────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={7}>
                                <Card>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Monthly Trend — Proposed vs Selected</Typography>
                                        <Typography variant="caption" color="text.secondary">Candidate activity across financial year</Typography>
                                        {monthKeys.length > 0 ? (
                                            <Chart options={areaChart.options} series={areaChart.series} type="area" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected period</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Recruitment Funnel</Typography>
                                        <Typography variant="caption" color="text.secondary">Proposed → Interviewed → Selected → Rejected</Typography>
                                        {totalProposed > 0 ? (
                                            <Chart options={funnelDonut.options} series={funnelDonut.series} type="donut" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected period</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Charts Row 2 ──────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={7}>
                                <Card>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Activity Breakdown by Recruiter</Typography>
                                        <Typography variant="caption" color="text.secondary">Proposed · Interviewed · Selected · Rejected</Typography>
                                        {recruiterNames.length > 0 ? (
                                            <Chart options={stackedBar.options} series={stackedBar.series} type="bar" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected period</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Share by Recruiter</Typography>
                                        <Typography variant="caption" color="text.secondary">Top 6 by candidates proposed</Typography>
                                        {top6Recruiters.length > 0 && top6Recruiters.some(([, v]) => v.proposed > 0) ? (
                                            <Chart options={recruiterDonut.options} series={recruiterDonut.series} type="donut" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Top Performers ────────────────────────────────────── */}
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <Card>
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                        <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5, color: "#FECB03", verticalAlign: "text-bottom" }} />
                                        Top Performers (YTD)
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {topPerformers.map(([name, stats], i) => (
                                            <Grid item xs={12} sm={6} md={2.4} key={name}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, p: 1.2, bgcolor: "#ffffff", borderRadius: 1.5 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: i < 3 ? `${medalColors[i]}20` : "#F1F5F9", color: i < 3 ? medalColors[i] : "#94A3B8", fontSize: "0.75rem", fontWeight: 800 }}>
                                                        #{i + 1}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={600} fontSize="0.8rem" noWrap>{name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {stats.selected} selected · {stats.proposed} proposed
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {rows.length === 0 && (
                                        <Typography fontSize="0.8rem" color="text.secondary">No data</Typography>
                                    )}
                                </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                    </>
                ) : (
                    /* ── Table Grid View ────────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <MaterialTable
                            columns={columns}
                            data={rows}
                            options={{
                                maxBodyHeight: "calc(100vh - 280px)",
                                minBodyHeight: "350px",
                                sorting: true,
                                search: true,
                                searchFieldAlignment: "right",
                                searchAutoFocus: true,
                                searchFieldVariant: "standard",
                                filtering: false,
                                exportButton: true,
                                exportAllData: true,
                                paging: true,
                                pageSizeOptions: [25, 50, 100],
                                pageSize: 25,
                                actionsColumnIndex: -1,
                                draggable: false,
                                columnsButton: true,
                                headerStyle: {
                                    backgroundColor: "#fff",
                                    color: "#64748B",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    whiteSpace: "nowrap",
                                    padding: "10px",
                                },
                            }}
                            title="YTD Activity Log"
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default YTDClosuresNewReport;
